import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { ROLE_META, SIDEBAR_NAV } from "@/data/sidebars";

const ROLE_GRADIENTS = {
  super_admin: {
    bg: "linear-gradient(180deg, #ffffff 0%, #fafaf9 100%)",
    shadow: "rgba(15, 23, 42, 0.06)",
    accent: "from-stone-600 to-slate-700",
    accentShadow: "shadow-slate-300/40",
    subtitleColor: "text-slate-400",
  },
  police: {
    bg: "linear-gradient(180deg, #ffffff 0%, #fafaf9 100%)",
    shadow: "rgba(15, 23, 42, 0.06)",
    accent: "from-stone-600 to-slate-700",
    accentShadow: "shadow-slate-300/40",
    subtitleColor: "text-slate-400",
  },
  fire: {
    bg: "linear-gradient(180deg, #ffffff 0%, #fafaf9 100%)",
    shadow: "rgba(15, 23, 42, 0.06)",
    accent: "from-stone-600 to-slate-700",
    accentShadow: "shadow-slate-300/40",
    subtitleColor: "text-slate-400",
  },
  ambulance: {
    bg: "linear-gradient(180deg, #ffffff 0%, #fafaf9 100%)",
    shadow: "rgba(15, 23, 42, 0.06)",
    accent: "from-stone-600 to-slate-700",
    accentShadow: "shadow-slate-300/40",
    subtitleColor: "text-slate-400",
  },
  hospital: {
    bg: "linear-gradient(180deg, #ffffff 0%, #fafaf9 100%)",
    shadow: "rgba(15, 23, 42, 0.06)",
    accent: "from-stone-600 to-slate-700",
    accentShadow: "shadow-slate-300/40",
    subtitleColor: "text-slate-400",
  },
};

function SidebarBody({ role, mobile = false, onNavigate }) {
  const meta = ROLE_META[role];
  const items = SIDEBAR_NAV[role] || [];
  const { user, logout, can } = useAuth();
  const visibleItems = items.filter((item) => !item.permission || can(item.permission));
  const navigate = useNavigate();
  const theme = ROLE_GRADIENTS[role] || ROLE_GRADIENTS.super_admin;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigate = (to) => {
    onNavigate?.(to);
  };

  return (
    <>
      {/* Brand */}
      <div className="flex items-center gap-3 border-b border-stone-200 px-5 py-5">
        <div className={`flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br ${theme.accent} shadow-lg ${theme.accentShadow}`}>
          <ShieldCheck size={20} strokeWidth={2} className="text-white" />
        </div>
        <div className="leading-tight">
          <p className="font-display text-[15px] font-bold text-slate-900 tracking-wide">Reyansh Tech</p>
          <p className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${theme.subtitleColor}`}>
            {meta?.title || "Console"}
          </p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-5 custom-scrollbar">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => handleNavigate(item.to)}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              cn(
                "group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[13px] font-medium transition-all duration-200",
                isActive
                  ? "bg-stone-100 text-slate-900 shadow-sm"
                  : "text-slate-500 hover:bg-stone-100 hover:text-slate-900",
              )
            }
          >
            <item.icon size={17} strokeWidth={1.75} className="text-current transition-colors" />
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User card */}
      <div className="border-t border-stone-200 p-3.5">
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 transition-colors hover:bg-stone-100">
          <div className="flex items-center gap-3">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${theme.accent} text-white text-[11px] font-bold shadow-md ${theme.accentShadow}`}>
              {(user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-slate-900">{user?.name}</p>
              <p className="truncate text-[10px] font-medium tracking-wider text-slate-400 uppercase">{user?.org_name || user?.role || "Operator"}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-all hover:bg-stone-200 hover:text-slate-700"
              title="Sign out"
            >
              <LogOut size={14} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Sidebar({ role, mobile = false, onNavigate }) {
  const theme = ROLE_GRADIENTS[role] || ROLE_GRADIENTS.super_admin;

  return (
    <aside
      data-testid={`sidebar-${role}`}
      className={cn(
        "shrink-0 flex-col overflow-hidden border-r border-stone-200 z-20",
        mobile ? "flex h-full w-full border-r-0" : "hidden md:flex fixed left-0 top-0 h-screen w-64",
      )}
      style={{
        background: theme.bg,
        boxShadow: `4px 0 30px ${theme.shadow}`,
      }}
    >
      <SidebarBody role={role} mobile={mobile} onNavigate={onNavigate} />
    </aside>
  );
}
